import { UcoastEl } from '@/scripts/core/UcoastEl'

interface BundleVariant {
  sku: string
  title: string
  default: boolean
  enabled: boolean
  variantId: string
  variantGid: string
  fakeSoldOut: boolean
}

interface BundleProduct {
  order: number
  title: string
  category: string
  imageSrc: string
  quantity: string
  variants: BundleVariant[]
  productId: string
  productGid: string
  productHandle: string
}

interface BundleCategory {
  id: string
  name: string
  order: number
  products: BundleProduct[]
}

interface DiscountTier {
  discount: number
  minQuantity: number
}

interface BundleDiscount {
  id: string
  tiers?: DiscountTier[]
  title: string
  endDate?: string | null
  discount: number
  products: any[]
  startDate: string
  discountType: 'tierDiscount' | 'allProducts'
}

interface KitData {
  parent: {
    sku: string
    title: string
    category: string
    imageSrc?: string
    productId: string
    variantId: string
    productGid: string
    variantGid: string
    productHandle: string
  }
  content: BundleCategory[]
  discount?: BundleDiscount
}

interface DefaultVariantInfo {
  variantId: string
  price: number
  quantity: number
  productId: string
  productHandle: string
}

export class BundleCardPrice extends UcoastEl {
  static htmlSelector = 'bundle-card-price'

  private kitData: KitData
  private batchData: any[] = []
  private priceContainer!: HTMLElement
  private handle: string

  constructor() {
    super()
    const kitDataString = this.getAttribute('data-kit') || '{}'
    this.handle = this.getAttribute('data-handle') || ''
    try {
      this.kitData = JSON.parse(kitDataString)
    } catch (error) {
      console.error('Error parsing kit data:', error)
      this.kitData = { parent: {} as any, content: [] }
    }

    // Parse batch data from attributes
    this.parseBatchData()
  }

  private parseBatchData(): void {
    // Read batch data from data-batch1, data-batch2, etc.
    for (let i = 1; i <= 5; i++) {
      const batchDataString = this.getAttribute(`data-batch${i}`) || '[]'
      try {
        const batchProducts = JSON.parse(batchDataString)
        if (Array.isArray(batchProducts) && batchProducts.length > 0) {
          this.batchData.push(...batchProducts)
        }
      } catch (error) {
        console.error(`Error parsing batch${i} data:`, error)
      }
    }
  }

  override connectedCallback() {
    super.connectedCallback()
    this.init()
  }

  private init(): void {
    this.priceContainer = this.querySelector('.bundle-price-display') as HTMLElement
    if (!this.priceContainer) {
      console.warn('Bundle price container not found')
      return
    }

    this.calculateAndDisplayPrice()
  }

  private calculateAndDisplayPrice(): void {
    // Extract default variants with their quantities
    const defaultVariants = this.extractDefaultVariants()
    
    if (defaultVariants.length === 0) {
      console.warn('No default variants found for bundle')
      return
    }

    // Calculate total price
    const totalPrice = this.calculateTotalPrice(defaultVariants)
    
    // Calculate discount
    const { finalPrice, discountPercent } = this.calculateDiscount(totalPrice, defaultVariants.length)
    
    // Display price
    this.displayPrice(totalPrice, finalPrice, discountPercent)
  }

  private extractDefaultVariants(): DefaultVariantInfo[] {
    const defaultVariants: DefaultVariantInfo[] = []

    // Iterate through all categories and extract default variants from all products
    for (const category of this.kitData.content) {
      for (const product of category.products) {
        const defaultVariant = product.variants.find(variant => variant.default && variant.enabled && variant.fakeSoldOut === false)


        if (defaultVariant) {
          let batchVariant: any = null
          this.batchData.forEach(p => {
            if (p.id?.toString() === product.productId) {
              batchVariant = p.variants.find((variant: any) => variant.id?.toString() === defaultVariant.variantId)
            }
          })

          const isAvailable = batchVariant?.available

          if (isAvailable) {
            // Get price from batch data
            const price = batchVariant?.price || 0
            const quantity = parseInt(product.quantity) || 1

            // Only add if price is valid
            if (price > 0) {
              defaultVariants.push({
                variantId: defaultVariant.variantId,
                price: price,
                quantity: quantity,
                productId: product.productId,
                productHandle: product.productHandle
              })
            }
          }
        }
      }
    }

    return defaultVariants
  }

  private calculateTotalPrice(defaultVariants: DefaultVariantInfo[]): number {
    return defaultVariants.reduce((total, variant) => {
      return total + (variant.price * variant.quantity)
    }, 0)
  }

  private calculateDiscount(totalPrice: number, totalItems: number): { finalPrice: number, discountPercent: number } {
    if (!this.kitData.discount) {
      return { finalPrice: totalPrice, discountPercent: 0 }
    }

    const discountConfig = this.kitData.discount

    if (discountConfig.discountType === 'allProducts') {
      // For allProducts type, apply discount if all items are selected
      // Since we're showing defaults, we'll apply the discount
      const discountPercent = discountConfig.discount || 0
      const multiplier = (100 - discountPercent) / 100
      return {
        finalPrice: totalPrice * multiplier,
        discountPercent: discountPercent
      }
    } else if (discountConfig.discountType === 'tierDiscount' && discountConfig.tiers) {
      // Find the highest applicable tier
      const sortedTiers = [...discountConfig.tiers].sort((a, b) => b.minQuantity - a.minQuantity)
      
      for (const tier of sortedTiers) {
        if (totalItems >= tier.minQuantity) {
          const multiplier = (100 - tier.discount) / 100
          return {
            finalPrice: totalPrice * multiplier,
            discountPercent: tier.discount
          }
        }
      }
    }

    // No discount applies
    return { finalPrice: totalPrice, discountPercent: 0 }
  }

  private displayPrice(originalPrice: number, finalPrice: number, discountPercent: number): void {
    const originalPriceFormatted = this.formatPrice(originalPrice / 100)
    const finalPriceFormatted = this.formatPrice(finalPrice / 100)
    
    if (discountPercent > 0) {
      // Show discounted price
      this.priceContainer.innerHTML = /*html*/ `
        <div class="price body-small price--on-sale">
          <span class="">
            <s class="price-item price-item--regular">$${originalPriceFormatted}</s><br/>
            <span class='price-item price-item--sale'>$${finalPriceFormatted}</span>
          </span>
        </div>
      `
      
      // Show discount tag
      this.updateDiscountTag(discountPercent)
    } else {
      // Show regular price
      this.priceContainer.innerHTML = /*html*/ `
        <div class="price body-small">
          <span class="price-item price-item--regular">$${originalPriceFormatted}</span>
        </div>
      `
    }
  }

  private updateDiscountTag(discountPercent: number): void {
    // Find the bundle product tag element
    const bundleTag = document.querySelector(`.bundle-product-tag-${this.handle}`) as HTMLElement
    
    if (bundleTag) {
      // Update text to show discount percentage
      bundleTag.textContent = `${Math.round(discountPercent)}% OFF`
      
      // Find the container (parent-parent) and show it by removing display:none
      const tagContainer = bundleTag.closest('.product-tag__container') as HTMLElement
      if (tagContainer) {
        tagContainer.style.display = 'flex'
      }
    }
  }

  private formatPrice(price: number): string {
    return price.toFixed(2).replace(/\.00$/, '')
  }

  // Public method to recalculate price (useful if kit data changes)
  public recalculatePrice(): void {
    this.calculateAndDisplayPrice()
  }

  // Public method to update kit data
  public updateKitData(newKitData: KitData): void {
    this.kitData = newKitData
    this.calculateAndDisplayPrice()
  }
} 